import { useMemo } from "react"
import { useSWRNoRevalidate } from "@nova/swr"
import { satelliteOutputFolder } from "@nova/util-s3"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectSatelliteFlightById } from "features/entity-caching/flights/selectors"
import { selectActiveCogOrtho } from "plugins/Satellite/layers/satelliteLayer"
import { indexCogIdForCloudFree } from "plugins/Satellite/provenance/provenanceTileSampling"

export type SatelliteSceneManifestEntry = {
    sceneId: string
    datetime: string
    cloudCoverage?: number | null
    contributedPixels?: number
}

// `scenes` entries index the top-level `scenes` array BY POSITION, not the scene's string `id` - older jobs emit a bare number.
type SatelliteManifestMosaic = {
    index?: string
    scenes?: (number | { id?: number; contributedPixels?: number })[]
}

type SatelliteManifest = {
    // Raw, unfiltered - index raster pixel values are positions into this array, so order must be preserved.
    scenes: SatelliteSceneManifestEntry[]
    mosaics: SatelliteManifestMosaic[]
}

// Untrusted external data written by satellite-mosaic-generator, not validated at write time - access defensively.
type SatelliteManifestFile = {
    scenes?: {
        id?: string
        datetime?: string
        cloudCoverage?: number | null
    }[]
    mosaics?: SatelliteManifestMosaic[]
}

async function loadManifest(
    orgId: string,
    projectId: string,
    flightId: string,
    isDemo: boolean
): Promise<SatelliteManifest> {
    const folder = satelliteOutputFolder(orgId, projectId, flightId)
    const manifestUrl = await createS3DownloadUrlInBrowser(`${folder}/manifest.json`, isDemo)
    // CloudFront auth is via signed cookies - a cross-origin fetch drops them without this.
    const manifest: SatelliteManifestFile = await fetch(manifestUrl, {
        credentials: isDemo ? "omit" : "include",
    }).then((res) => res.json())
    // Pixel values in the index raster are positions into this list - don't sort or filter it.
    const scenes = (manifest.scenes ?? [])
        .filter((scene) => scene?.id && scene?.datetime)
        .map((scene) => ({
            sceneId: scene.id!,
            datetime: scene.datetime!,
            cloudCoverage: scene.cloudCoverage,
        }))
    return { scenes, mosaics: manifest.mosaics ?? [] }
}

export function useSatelliteProvenanceData(flightId: string) {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const flight = useAppSelector(selectSatelliteFlightById(flightId))
    const isDemo = !!flight?.demoId

    const { data, error } = useSWRNoRevalidate<SatelliteManifest | undefined>(
        // manifest.json doesn't exist until the job finishes - a too-early 403 sticks until remount (non-revalidating key).
        orgId && projectId && flightId && flight && selectActiveCogOrtho(flight)
            ? ["satellite-provenance-manifest", orgId, projectId, flightId]
            : null,
        () => loadManifest(orgId!, projectId!, flightId, isDemo)
    )
    const manifest = data?.scenes

    // Matched by `index` filename, not an `options` field (manifest.json has none) - only cloudFree affects scene selection.
    const activeScenes = useMemo(() => {
        if (!data) return undefined
        const mosaic =
            data.mosaics.find((m) => m.index === indexCogIdForCloudFree(!!flight?.cloudFree)) ??
            (data.mosaics.length === 1 ? data.mosaics[0] : undefined)
        const mosaicScenes = mosaic?.scenes
        if (!mosaicScenes?.length) return data.scenes
        return mosaicScenes
            .map((entry) => {
                const index = typeof entry === "number" ? entry : entry?.id
                const scene = index !== undefined ? data.scenes[index] : undefined
                const contributedPixels =
                    typeof entry === "number" ? undefined : entry?.contributedPixels
                return scene && { ...scene, contributedPixels }
            })
            .filter(Boolean) as SatelliteSceneManifestEntry[]
    }, [data, flight?.cloudFree])

    const dateRange = useMemo(() => {
        if (!activeScenes?.length) return undefined
        const times = activeScenes.map((scene) => new Date(scene.datetime).getTime())
        return { first: new Date(Math.min(...times)), last: new Date(Math.max(...times)) }
    }, [activeScenes])

    return {
        manifest,
        activeScenes,
        dateRange,
        isLoading: !data && !error,
        error,
    }
}
